import { ReactNode } from "react";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Inbox } from "lucide-react";

/**
 * GENERIC thin-client tab scaffold — the reference pattern for EVERY tab
 * (Team now; Home, Drafts, More reuse it UNCHANGED). It standardizes the page
 * header + the four states (loading / empty / error+retry / success) and the
 * Receipt visual identity. It is data-source agnostic: a page passes a
 * `TabQueryLike` (one query, or several merged) and a render-prop for success.
 *
 * It contains NO domain logic and computes NO sensitive value — it only
 * switches on query state and renders children with the already-safe payload.
 */

/** Minimal query shape (matches a tRPC query, or a merge of several). */
export interface TabQueryLike<T> {
  isLoading: boolean;
  isError: boolean;
  data: T | undefined;
  error?: unknown;
  refetch: () => void;
}

export interface TabScaffoldProps<T> {
  title: string;
  subtitle?: ReactNode;
  /** Right-aligned header slot (e.g. a "Shared" pill). */
  headerRight?: ReactNode;
  /** Optional full-width app chrome rendered ABOVE the title (generic — any tab
   *  may pass one, e.g. Home's AppTopBar). Keep this a plain slot: no
   *  tab-specific logic belongs in TabScaffold. */
  topBar?: ReactNode;
  state: TabQueryLike<T>;
  /** Optional: treat a successfully-loaded payload as empty. */
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyHint?: string;
  emptyIcon?: ReactNode;
  /** Optional action(s) rendered below the empty hint (e.g. a "Switch to your
   *  team" button). Generic slot — no domain logic lives in the scaffold. */
  emptyAction?: ReactNode;
  /** Render-prop: receives the already-safe payload on success. */
  children: (data: T) => ReactNode;
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm">
      {children}
    </div>
  );
}

export function TabScaffold<T>({
  title,
  subtitle,
  headerRight,
  topBar,
  state,
  isEmpty,
  emptyTitle,
  emptyHint,
  emptyIcon,
  emptyAction,
  children,
}: TabScaffoldProps<T>) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const resolvedEmptyTitle =
    emptyTitle ?? (isAr ? "لا يوجد شيء هنا بعد" : "Nothing here yet");
  return (
    <div className="text-foreground">
      {topBar ? <div data-testid="tab-topbar">{topBar}</div> : null}
      <header className="mx-auto w-full max-w-lg px-4 pt-3 pb-2">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-[26px] leading-tight sm:text-3xl tracking-[-0.01em]">
              {title}
            </h1>
            {subtitle ? (
              <div className="text-sm text-muted-foreground mt-0.5">{subtitle}</div>
            ) : null}
          </div>
          {headerRight ? <div className="shrink-0 pb-0.5">{headerRight}</div> : null}
        </div>
      </header>

      <main className="mx-auto w-full max-w-lg px-4 pb-2 space-y-5">
        {/* 1) LOADING */}
        {state.isLoading || (!state.isError && state.data === undefined) ? (
          <div className="space-y-5" data-testid="tab-loading" aria-busy="true">
            <Skeleton className="h-28 w-full rounded-3xl" />
            <Skeleton className="h-40 w-full rounded-3xl" />
            <Skeleton className="h-40 w-full rounded-3xl" />
          </div>
        ) : /* 2) ERROR + RETRY */ state.isError ? (
          <div data-testid="tab-error">
            <Card>
              <div className="flex flex-col items-start gap-3">
                <div className="flex items-center gap-2 text-foreground">
                  <AlertCircle className="h-5 w-5 text-coral" />
                  <span className="font-medium">
                    {isAr ? `تعذّر تحميل ${title}` : `Couldn’t load ${title.toLowerCase()}`}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isAr
                    ? "حدث خطأ أثناء جلب هذه الصفحة. يرجى المحاولة مرة أخرى."
                    : "Something went wrong fetching this view. Please try again."}
                </p>
                <Button onClick={() => state.refetch()} className="rounded-full">
                  {isAr ? "إعادة المحاولة" : "Retry"}
                </Button>
              </div>
            </Card>
          </div>
        ) : /* 3) EMPTY */ isEmpty && state.data !== undefined && isEmpty(state.data) ? (
          <div data-testid="tab-empty">
            <Card>
              <div className="flex flex-col items-center text-center gap-2 py-6">
                <div className="text-coral">{emptyIcon ?? <Inbox className="h-7 w-7" />}</div>
                <div className="font-medium">{resolvedEmptyTitle}</div>
                {emptyHint ? (
                  <div className="text-sm text-muted-foreground max-w-sm">{emptyHint}</div>
                ) : null}
                {emptyAction ? <div className="mt-2 flex flex-col items-center gap-2">{emptyAction}</div> : null}
              </div>
            </Card>
          </div>
        ) : (
          /* 4) SUCCESS */
          <div data-testid="tab-success" className="space-y-2.5">{children(state.data as T)}</div>
        )}
      </main>
    </div>
  );
}

/** Merge several queries into one `TabQueryLike` for a tab that needs more than
 *  one source (e.g. Team = pulse + sharedReceipts). Loading until ALL load;
 *  error if ANY errors; retry refetches all; data present only when all are. */
export function mergeTabQueries<T extends Record<string, TabQueryLike<any>>>(
  queries: T,
): TabQueryLike<{ [K in keyof T]: NonNullable<T[K]["data"]> }> {
  const entries = Object.entries(queries);
  const isLoading = entries.some(([, q]) => q.isLoading);
  const isError = entries.some(([, q]) => q.isError);
  const allLoaded = entries.every(([, q]) => q.data !== undefined);
  const data = allLoaded
    ? (Object.fromEntries(entries.map(([k, q]) => [k, q.data])) as any)
    : undefined;
  return {
    isLoading,
    isError,
    data,
    refetch: () => entries.forEach(([, q]) => q.refetch()),
  };
}
